import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  Channel,
  ChannelListFilter,
  SortChannelList,
  TypedListRequestQuery,
  TypedListResponse
} from '@skyrise-eng/types';
import { baseQuery } from './baseQuery';
import { buildQueryString } from '@/utils';
import { TAG } from '@/constants';
import type {
  CreateChannelPayload,
  UpdateChannelPayload,
  ChannelListItem,
  GetChannelMetricsQuery,
  ChannelMetricsResponse
} from '@/models';

const CHANNEL_URL = 'channels/api/v1';

export interface DspOption {
  dspName: string;
  feePercentage: number;
  isActive?: boolean;
}

export interface DspOptionsResponse {
  dsps: DspOption[];
  count: number;
}

export interface ChannelOption {
  key: string;
  label: string;
  isActive?: boolean;
  /** Prefilled into a new line item's targeting details when this channel is picked. */
  defaultTargetingDetails?: string;
  /** DSP preselected on a new line item for this channel. */
  defaultDsp?: string;
}

export interface ChannelOptionsResponse {
  channels: ChannelOption[];
}

export interface KpiOptionsResponse {
  kpis: string[];
}

// Define a service using a base URL and expected endpoints
export const channelApi = createApi({
  reducerPath: 'channelApi',
  tagTypes: [TAG.CHANNEL, TAG.CHANNELS_BUDGET, TAG.CHANNEL_METRICS],
  baseQuery,
  endpoints: (builder) => ({
    getDspOptions: builder.query<DspOptionsResponse, void>({
      query: () => `${CHANNEL_URL}/dsp-options`,
      providesTags: () => [TAG.CHANNEL]
    }),
    getChannelOptions: builder.query<ChannelOptionsResponse, void>({
      query: () => `${CHANNEL_URL}/options`,
      providesTags: () => [TAG.CHANNEL]
    }),
    getKpiOptions: builder.query<KpiOptionsResponse, void>({
      query: () => `${CHANNEL_URL}/kpi-options`,
      providesTags: () => [TAG.CHANNEL]
    }),
    getChannelList: builder.query<
      TypedListResponse<ChannelListItem>,
      TypedListRequestQuery<ChannelListFilter, SortChannelList>
    >({
      query: (params) => `${CHANNEL_URL}?${buildQueryString(params)}`,
      providesTags: () => [TAG.CHANNEL]
    }),
    getChannelMetrics: builder.query<ChannelMetricsResponse, GetChannelMetricsQuery>({
      query: (params) => `${CHANNEL_URL}/metrics?${buildQueryString(params)}`,
      providesTags: () => [TAG.CHANNEL_METRICS]
    }),
    getChannelsBudget: builder.query<number, { mediaPlanId?: string; excludeChannel?: string }>({
      async queryFn(args, _api, _extraOptions, fetchWithBQ) {
        const buildBudgetUrl = (excludeChannel?: string) => {
          const params = new URLSearchParams();
          if (args.mediaPlanId) params.set('mediaPlan', String(args.mediaPlanId));
          if (excludeChannel) params.set('excludeChannel', excludeChannel);
          return `${CHANNEL_URL}/budget?${params.toString()}`;
        };

        const primaryUrl = buildBudgetUrl(args.excludeChannel);


        let result = await fetchWithBQ(primaryUrl);

        const errorMessage =
          result.error && typeof result.error === 'object' && 'data' in result.error
            ? String((result.error as { data?: { message?: string } }).data?.message ?? '')
            : '';

        if (result.error && args.excludeChannel && errorMessage.toLowerCase().includes('not found')) {
          const fallbackUrl = buildBudgetUrl(undefined);
          result = await fetchWithBQ(fallbackUrl);
        }

        if (result.error) {
          return { error: result.error as { status: number; data: unknown } };
        }
        return { data: result.data as number };
      },
      providesTags: () => [TAG.CHANNELS_BUDGET]
    }),
    createChannel: builder.mutation<Channel, CreateChannelPayload>({
      query: (channel) => ({
        url: CHANNEL_URL,
        method: 'POST',
        body: channel
      }),
      invalidatesTags: [TAG.CHANNEL, TAG.CHANNELS_BUDGET]
    }),
    updateChannel: builder.mutation<Channel, UpdateChannelPayload>({
      query: ({ _id, ...rest }) => {
        return {
          url: `${CHANNEL_URL}/${_id}`,
          method: 'PUT',
          body: rest
        };
      },
      invalidatesTags: [TAG.CHANNEL, TAG.CHANNELS_BUDGET]
    }),
    /**
     * Removes a line item. Once the line is booked the API requires a reason,
     * cancels its booking and keeps a record of it on the plan.
     */
    deleteChannel: builder.mutation<Channel, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `${CHANNEL_URL}/${id}`,
        method: 'DELETE',
        params: reason ? { reason } : undefined
      }),
      invalidatesTags: [TAG.CHANNEL, TAG.CHANNELS_BUDGET]
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetDspOptionsQuery,
  useGetChannelOptionsQuery,
  useGetKpiOptionsQuery,
  useGetChannelListQuery,
  useGetChannelsBudgetQuery,
  useCreateChannelMutation,
  useUpdateChannelMutation,
  useDeleteChannelMutation,
  useGetChannelMetricsQuery
} = channelApi;
